export function getFileIcon(fileName: string): string {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'xls':
    case 'xlsx':
      return '/img/excel.svg';
    case 'doc':
    case 'docx':
    case 'pages':
    case 'odt':
      return '/img/word.svg';
    default:
      return '/img/document-upload.svg';
  }
}

type CurateIconResource = {
  Title?: string;
  title?: string;
  extension?: string;
  kind?: string;
};

const KNOWN_FILE_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "pages",
  "odt",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "mp3",
  "wav",
  "mp4",
  "mov",
  "zip",
  "txt",
]);

export const resolveExtension = (resource: CurateIconResource): string => {
  const fromField = resource.extension?.replace(/^\./, "").toLowerCase();
  if (fromField) {
    return fromField;
  }

  const title = resource.Title ?? resource.title ?? "";
  const match = title.match(/\.([a-z0-9]+)$/i);
  const fromTitle = match?.[1]?.toLowerCase() ?? "";
  // Titles such as "… - GOV.UK" must not be treated as a .uk file.
  return KNOWN_FILE_EXTENSIONS.has(fromTitle) ? fromTitle : "";
};

const isCurateWebLink = (resource: CurateIconResource): boolean => {
  const kind = resource.kind?.toLowerCase();
  return kind === "link" || kind === "source";
};

/** Same icons as evidence chips, plus the browser mark for URL Bank links. A linked PDF keeps the file icon. */
export function getCurateResourceIcon(resource: CurateIconResource): string {
  const ext = resolveExtension(resource);
  if (!ext && isCurateWebLink(resource)) {
    return "/img/browser.svg";
  }

  return getFileIcon(ext ? `file.${ext}` : (resource.Title ?? resource.title ?? ""));
}

/** Office-style type labels: WORD, EXCEL, POWERPOINT, PDF, LINK. A linked PDF is labelled PDF, not LINK. */
export function getCurateResourceTypeLabel(resource: CurateIconResource): string {
  const ext = resolveExtension(resource);
  if (!ext && isCurateWebLink(resource)) {
    return "LINK";
  }

  switch (ext) {
    case "doc":
    case "docx":
    case "pages":
    case "odt":
      return "WORD";
    case "xls":
    case "xlsx":
      return "EXCEL";
    case "ppt":
    case "pptx":
      return "POWERPOINT";
    case "pdf":
      return "PDF";
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
      return "IMAGE";
    case "mp3":
    case "wav":
      return "AUDIO";
    case "mp4":
    case "mov":
      return "VIDEO";
    case "zip":
      return "ARCHIVE";
    case "txt":
      return "TEXT";
    default:
      return ext ? ext.toUpperCase() : "FILE";
  }
}
